import React, { useEffect, useState } from 'react';
import AdminAppBar from './AdminAppBar';
import { collection, onSnapshot, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import { FaSearch, FaTimes, FaEdit, FaTrash, FaFish, FaShoppingCart, FaCheckCircle, FaBox, FaUtensils } from 'react-icons/fa';
import { GiHerbsBundle } from "react-icons/gi";

const AdminHomeScreen = () => {
  const [guppyStats, setGuppyStats] = useState({ totalGuppies: 0, soldGuppies: 0 });
  const [orderStats, setOrderStats] = useState({ totalOrders: 0, pendingOrders: 0 });
  
  const [activeTab, setActiveTab] = useState('guppies'); // 'guppies', 'plants', 'aquariums', 'food'
  
  const [guppies, setGuppies] = useState([]);
  const [plants, setPlants] = useState([]);
  const [aquariums, setAquariums] = useState([]);
  const [food, setFood] = useState([]);
  
  const [filteredItems, setFilteredItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingItemId, setDeletingItemId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [savingItemId, setSavingItemId] = useState(null);

  // Fetch all collections
  useEffect(() => {
    // Guppies
    const guppyUnsubscribe = onSnapshot(collection(db, 'guppies'), (snapshot) => {
      const list = snapshot.docs.map(doc => ({
        id: doc.id, ...doc.data(),
        couplecount: parseInt(doc.data().couplecount, 10) || 0,
        femalecount: parseInt(doc.data().femalecount, 10) || 0,
        malecount: parseInt(doc.data().malecount, 10) || 0,
        guppynumber: parseInt(doc.data().guppynumber, 10) || 0,
        coupleprice: parseFloat(doc.data().coupleprice) || 0,
        femaleprice: parseFloat(doc.data().femaleprice) || 0,
        maleprice: parseFloat(doc.data().maleprice) || 0,
      }));
      setGuppies(list);
      let sold = 0;
      list.forEach(g => { if (g.sold_status) sold++; });
      setGuppyStats({ totalGuppies: list.length, soldGuppies: sold });
    });

    // Plants
    const plantUnsubscribe = onSnapshot(collection(db, 'plants'), (snapshot) => {
      setPlants(snapshot.docs.map(doc => ({
        id: doc.id, ...doc.data(),
        price: parseFloat(doc.data().price) || 0,
        quantity: parseInt(doc.data().quantity, 10) || 0,
        tags: parseInt(doc.data().tags, 10) || 0,
      })));
    });

    // Aquariums
    const aquariumUnsubscribe = onSnapshot(collection(db, 'aquariums'), (snapshot) => {
      setAquariums(snapshot.docs.map(doc => ({
        id: doc.id, ...doc.data(),
        price: parseFloat(doc.data().price) || 0,
        quantity: parseFloat(doc.data().quantity) || 0,
      })));
    });

    // Food
    const foodUnsubscribe = onSnapshot(collection(db, 'food'), (snapshot) => {
      setFood(snapshot.docs.map(doc => ({
        id: doc.id, ...doc.data(),
        flakesPrice: parseFloat(doc.data().flakesPrice) || 0,
        powderPrice: parseFloat(doc.data().powderPrice) || 0,
        flakesQuantity: parseInt(doc.data().flakesQuantity, 10) || 0,
        powderQuantity: parseInt(doc.data().powderQuantity, 10) || 0,
      })));
    });

    // Orders
    const orderUnsubscribe = onSnapshot(collection(db, 'orders'), (snapshot) => {
      let total = 0;
      let pending = 0;
      snapshot.forEach(doc => {
        total++;
        if (doc.data().status === 'Pending') pending++;
      });
      setOrderStats({ totalOrders: total, pendingOrders: pending });
    });

    return () => {
      guppyUnsubscribe();
      plantUnsubscribe();
      aquariumUnsubscribe();
      foodUnsubscribe();
      orderUnsubscribe();
    };
  }, []);

  // Update filtered items when activeTab, search, or source data changes
  useEffect(() => {
    let sourceData = [];
    if (activeTab === 'guppies') sourceData = guppies;
    if (activeTab === 'plants') sourceData = plants;
    if (activeTab === 'aquariums') sourceData = aquariums;
    if (activeTab === 'food') sourceData = food;

    if (searchQuery) {
      setFilteredItems(sourceData.filter(item => {
        const query = searchQuery.toLowerCase();
        const nameMatch = item.name && item.name.toLowerCase().includes(query);
        const numberMatch = item.guppynumber && item.guppynumber.toString().includes(query);
        return nameMatch || numberMatch;
      }));
    } else {
      setFilteredItems(sourceData);
    }
  }, [activeTab, searchQuery, guppies, plants, aquariums, food]);

  const deleteCloudinaryImage = async (imageUrl) => {
    if (!imageUrl) return;
    try {
      const functions = getFunctions();
      const deleteImageFn = httpsCallable(functions, 'deleteCloudinaryImage');
      await deleteImageFn({ imageUrl });
      console.log('Image deleted from Cloudinary');
    } catch (error) {
      console.error('Error deleting from Cloudinary:', error);
    }
  };

  const handleDeleteItem = async (item) => {
    if (showDeleteConfirm !== item.id) {
      setShowDeleteConfirm(item.id);
      return;
    }

    setDeletingItemId(item.id);
    try {
      const collectionName = activeTab === 'food' ? 'food' : activeTab;
      await deleteDoc(doc(db, collectionName, item.id));
      
      // Attempt to delete image(s) from cloudinary
      if (item.image) await deleteCloudinaryImage(item.image);
      if (item.maleimage) await deleteCloudinaryImage(item.maleimage);
      if (item.femaleimage) await deleteCloudinaryImage(item.femaleimage);
      
      alert('Item deleted successfully!');
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting item: ', error);
      alert(`Failed to delete item: ${error.message || 'Unknown error'}`);
    } finally {
      setDeletingItemId(null);
    }
  };

  const handleEditItem = async (id, updatedData) => {
    setSavingItemId(id);
    try {
      if (!updatedData.name || updatedData.name.trim() === '') {
        throw new Error('Name is required');
      }

      let parsedData = { ...updatedData };
      if (activeTab === 'guppies') {
        parsedData.coupleprice = parseFloat(updatedData.coupleprice) || 0;
        parsedData.femaleprice = parseFloat(updatedData.femaleprice) || 0;
        parsedData.maleprice = parseFloat(updatedData.maleprice) || 0;
        parsedData.couplecount = Math.max(0, parseInt(updatedData.couplecount, 10) || 0);
        parsedData.guppynumber = parseInt(updatedData.guppynumber, 10) || 0;
        parsedData.femalecount = Math.max(0, parseInt(updatedData.femalecount, 10) || 0);
        parsedData.malecount = Math.max(0, parseInt(updatedData.malecount, 10) || 0);
        const hasStock = parsedData.couplecount > 0 || parsedData.femalecount > 0 || parsedData.malecount > 0;
        parsedData.sold_status = !hasStock;
      } else if (activeTab === 'plants') {
        parsedData.price = parseFloat(updatedData.price) || 0;
        parsedData.quantity = parseInt(updatedData.quantity, 10) || 0;
        parsedData.tags = parseInt(updatedData.tags, 10) || 0;
        parsedData.sold_status = parsedData.quantity <= 0;
      } else if (activeTab === 'aquariums') {
        parsedData.price = parseFloat(updatedData.price) || 0;
        parsedData.quantity = parseFloat(updatedData.quantity) || 0;
        parsedData.available = parsedData.quantity > 0;
      } else if (activeTab === 'food') {
        parsedData.flakesPrice = parseFloat(updatedData.flakesPrice) || 0;
        parsedData.powderPrice = parseFloat(updatedData.powderPrice) || 0;
        parsedData.flakesQuantity = parseInt(updatedData.flakesQuantity, 10) || 0;
        parsedData.powderQuantity = parseInt(updatedData.powderQuantity, 10) || 0;
        const hasStock = parsedData.flakesQuantity > 0 || parsedData.powderQuantity > 0;
        parsedData.sold_status = !hasStock;
      }

      const collectionName = activeTab === 'food' ? 'food' : activeTab;
      await updateDoc(doc(db, collectionName, id), parsedData);
      alert('Item updated successfully!');
      setEditingItem(null);
    } catch (error) {
      console.error('Error updating item: ', error);
      alert(`Failed to update item: ${error.message || 'Unknown error'}`);
    } finally {
      setSavingItemId(null);
    }
  };

  const openEditForm = (item) => {
    setEditingItem(item);
    setFormData({ ...item });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleEditItem(editingItem.id, formData);
  };

  const renderTabs = () => (
    <div style={{ display: 'flex', gap: '12px', overflowX: 'auto', marginBottom: '24px', paddingBottom: '8px' }}>
      {[
        { id: 'guppies', label: 'Guppies', icon: FaFish, color: '#06b6d4' },
        { id: 'plants', label: 'Plants', icon: GiHerbsBundle, color: '#22c55e' },
        { id: 'aquariums', label: 'Aquariums', icon: FaBox, color: '#3b82f6' },
        { id: 'food', label: 'Food', icon: FaUtensils, color: '#f97316' }
      ].map(tab => (
        <button
          key={tab.id}
          onClick={() => {
            setActiveTab(tab.id);
            setSearchQuery('');
            setEditingItem(null);
          }}
          style={{
            padding: '12px 24px',
            background: activeTab === tab.id ? `linear-gradient(135deg, ${tab.color}40, transparent)` : 'rgba(30, 41, 59, 0.6)',
            border: `1px solid ${activeTab === tab.id ? tab.color : 'rgba(71, 85, 105, 0.4)'}`,
            borderRadius: '16px',
            color: activeTab === tab.id ? tab.color : '#94a3b8',
            fontSize: '16px',
            fontWeight: '700',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.3s ease',
            whiteSpace: 'nowrap'
          }}
        >
          <tab.icon /> {tab.label}
        </button>
      ))}
    </div>
  );

  const renderCardDetails = (item) => {
    if (activeTab === 'guppies') {
      return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
          <div style={{ padding: '12px', background: 'rgba(6, 182, 212, 0.1)', borderRadius: '12px', border: '1px solid rgba(6, 182, 212, 0.2)' }}>
            <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>Number</div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#06b6d4' }}>{item.guppynumber ?? 'N/A'}</div>
          </div>
          <div style={{ padding: '12px', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '12px', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
            <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>Total Stock</div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#10b981' }}>
              {(item.couplecount||0) + (item.femalecount||0) + (item.malecount||0)}
            </div>
          </div>
          <div style={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
             <div style={{ display: 'flex', justifyContent: 'space-between', color: '#cbd5e1' }}>
               <span>Couple:</span><span style={{ fontWeight: '700', color: '#fff' }}>DA {Number(item.coupleprice||0).toFixed(2)} ({item.couplecount})</span>
             </div>
             <div style={{ display: 'flex', justifyContent: 'space-between', color: '#cbd5e1' }}>
               <span>Female:</span><span style={{ fontWeight: '700', color: '#fff' }}>DA {Number(item.femaleprice||0).toFixed(2)} ({item.femalecount})</span>
             </div>
             <div style={{ display: 'flex', justifyContent: 'space-between', color: '#cbd5e1' }}>
               <span>Male:</span><span style={{ fontWeight: '700', color: '#fff' }}>DA {Number(item.maleprice||0).toFixed(2)} ({item.malecount})</span>
             </div>
          </div>
        </div>
      );
    } else if (activeTab === 'plants') {
      return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
          <div style={{ padding: '12px', background: 'rgba(34, 197, 94, 0.1)', borderRadius: '12px', border: '1px solid rgba(34, 197, 94, 0.2)' }}>
            <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>Price</div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#22c55e' }}>DA {item.price}</div>
          </div>
          <div style={{ padding: '12px', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '12px', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
            <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>Quantity</div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#10b981' }}>{item.quantity}</div>
          </div>
          <div style={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px', color: '#cbd5e1' }}>
             <div>Type: <span style={{color: '#fff'}}>{item.type || 'N/A'}</span></div>
             <div>Care: <span style={{color: '#fff'}}>{item.care || 'N/A'}</span></div>
             <div>Branches/Tags: <span style={{color: '#fff'}}>{item.tags || '0'}</span></div>
          </div>
        </div>
      );
    } else if (activeTab === 'aquariums') {
      return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
          <div style={{ padding: '12px', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '12px', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
            <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>Price</div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#3b82f6' }}>DA {item.price}</div>
          </div>
          <div style={{ padding: '12px', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '12px', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
            <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>Quantity</div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#10b981' }}>{item.quantity}</div>
          </div>
          <div style={{ gridColumn: 'span 2', color: '#cbd5e1', fontSize: '14px' }}>
            Size: <span style={{color: '#fff'}}>{item.size || 'N/A'}</span>
          </div>
        </div>
      );
    } else if (activeTab === 'food') {
      return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px', marginBottom: '20px' }}>
          <div style={{ padding: '12px', background: 'rgba(249, 115, 22, 0.1)', borderRadius: '12px', border: '1px solid rgba(249, 115, 22, 0.2)' }}>
             <div style={{ fontSize: '14px', color: '#f97316', fontWeight: '700', marginBottom: '8px' }}>Flakes</div>
             <div style={{ display: 'flex', justifyContent: 'space-between', color: '#cbd5e1', fontSize: '14px' }}>
               <span>Price: <span style={{color: '#fff'}}>DA {item.flakesPrice}</span></span>
               <span>Qty: <span style={{color: '#fff'}}>{item.flakesQuantity}</span></span>
             </div>
          </div>
          <div style={{ padding: '12px', background: 'rgba(249, 115, 22, 0.1)', borderRadius: '12px', border: '1px solid rgba(249, 115, 22, 0.2)' }}>
             <div style={{ fontSize: '14px', color: '#f97316', fontWeight: '700', marginBottom: '8px' }}>Powder</div>
             <div style={{ display: 'flex', justifyContent: 'space-between', color: '#cbd5e1', fontSize: '14px' }}>
               <span>Price: <span style={{color: '#fff'}}>DA {item.powderPrice}</span></span>
               <span>Qty: <span style={{color: '#fff'}}>{item.powderQuantity}</span></span>
             </div>
          </div>
        </div>
      );
    }
  };

  const getStatusInfo = (item) => {
    let isSoldOut = false;
    if (activeTab === 'guppies') {
      isSoldOut = item.sold_status;
    } else if (activeTab === 'plants') {
      isSoldOut = item.quantity <= 0 || item.sold_status;
    } else if (activeTab === 'aquariums') {
      isSoldOut = item.quantity <= 0 || !item.available;
    } else if (activeTab === 'food') {
      isSoldOut = item.sold_status;
    }
    return {
      isSoldOut,
      text: isSoldOut ? 'Sold Out' : 'Available',
      color: isSoldOut ? '#f87171' : '#10b981',
      bg: isSoldOut ? 'rgba(239, 68, 68, 0.3)' : 'rgba(16, 185, 129, 0.3)',
      border: isSoldOut ? 'rgba(239, 68, 68, 0.5)' : 'rgba(16, 185, 129, 0.5)'
    };
  };

  const renderEditFields = () => {
    const inputStyle = {
      width: '100%', padding: '12px', background: 'rgba(15, 23, 42, 0.6)', 
      border: '1px solid rgba(71, 85, 105, 0.5)', borderRadius: '12px',
      color: 'white', marginBottom: '16px', boxSizing: 'border-box'
    };
    const labelStyle = { display: 'block', marginBottom: '8px', color: '#cbd5e1', fontSize: '14px' };

    let fields = [];
    fields.push(
      <div key="name">
        <label style={labelStyle}>Name</label>
        <input type="text" name="name" value={formData.name || ''} onChange={handleInputChange} style={inputStyle} />
      </div>
    );

    if (activeTab === 'guppies') {
      fields.push(
        <div key="g-num">
          <label style={labelStyle}>Number</label>
          <input type="number" name="guppynumber" value={formData.guppynumber || ''} onChange={handleInputChange} style={inputStyle} />
        </div>,
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }} key="g-couple">
           <div><label style={labelStyle}>Couple Count</label><input type="number" name="couplecount" value={formData.couplecount || ''} onChange={handleInputChange} style={inputStyle} /></div>
           <div><label style={labelStyle}>Couple Price</label><input type="number" name="coupleprice" value={formData.coupleprice || ''} onChange={handleInputChange} style={inputStyle} /></div>
        </div>,
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }} key="g-female">
           <div><label style={labelStyle}>Female Count</label><input type="number" name="femalecount" value={formData.femalecount || ''} onChange={handleInputChange} style={inputStyle} /></div>
           <div><label style={labelStyle}>Female Price</label><input type="number" name="femaleprice" value={formData.femaleprice || ''} onChange={handleInputChange} style={inputStyle} /></div>
        </div>,
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }} key="g-male">
           <div><label style={labelStyle}>Male Count</label><input type="number" name="malecount" value={formData.malecount || ''} onChange={handleInputChange} style={inputStyle} /></div>
           <div><label style={labelStyle}>Male Price</label><input type="number" name="maleprice" value={formData.maleprice || ''} onChange={handleInputChange} style={inputStyle} /></div>
        </div>
      );
    } else if (activeTab === 'plants') {
      fields.push(
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }} key="p-main">
           <div><label style={labelStyle}>Price</label><input type="number" name="price" value={formData.price || ''} onChange={handleInputChange} style={inputStyle} /></div>
           <div><label style={labelStyle}>Quantity</label><input type="number" name="quantity" value={formData.quantity || ''} onChange={handleInputChange} style={inputStyle} /></div>
        </div>,
        <div key="p-tags"><label style={labelStyle}>Tags/Branches</label><input type="number" name="tags" value={formData.tags || ''} onChange={handleInputChange} style={inputStyle} /></div>,
        <div key="p-type"><label style={labelStyle}>Type</label><input type="text" name="type" value={formData.type || ''} onChange={handleInputChange} style={inputStyle} /></div>,
        <div key="p-care"><label style={labelStyle}>Care Level</label>
          <select name="care" value={formData.care || ''} onChange={handleInputChange} style={inputStyle}>
            <option value="">Select Care Level</option>
            <option value="سهل">سهل (Easy)</option>
            <option value="متوسط">متوسط (Medium)</option>
            <option value="صعب">صعب (Hard)</option>
          </select>
        </div>
      );
    } else if (activeTab === 'aquariums') {
      fields.push(
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }} key="a-main">
           <div><label style={labelStyle}>Price</label><input type="number" name="price" value={formData.price || ''} onChange={handleInputChange} style={inputStyle} /></div>
           <div><label style={labelStyle}>Quantity</label><input type="number" name="quantity" value={formData.quantity || ''} onChange={handleInputChange} style={inputStyle} /></div>
        </div>,
        <div key="a-size"><label style={labelStyle}>Size</label><input type="text" name="size" value={formData.size || ''} onChange={handleInputChange} style={inputStyle} /></div>
      );
    } else if (activeTab === 'food') {
      fields.push(
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }} key="f-flakes">
           <div><label style={labelStyle}>Flakes Price</label><input type="number" name="flakesPrice" value={formData.flakesPrice || ''} onChange={handleInputChange} style={inputStyle} /></div>
           <div><label style={labelStyle}>Flakes Quantity</label><input type="number" name="flakesQuantity" value={formData.flakesQuantity || ''} onChange={handleInputChange} style={inputStyle} /></div>
        </div>,
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }} key="f-powder">
           <div><label style={labelStyle}>Powder Price</label><input type="number" name="powderPrice" value={formData.powderPrice || ''} onChange={handleInputChange} style={inputStyle} /></div>
           <div><label style={labelStyle}>Powder Quantity</label><input type="number" name="powderQuantity" value={formData.powderQuantity || ''} onChange={handleInputChange} style={inputStyle} /></div>
        </div>
      );
    }

    return fields;
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0e1e 0%, #1a1f2e 25%, #0f172a 50%, #1a1f2e 75%, #0a0e1e 100%)',
      backgroundSize: '400% 400%',
      position: 'relative',
      width: '100%',
      overflowX: 'hidden',
      animation: 'gradientShift 15s ease infinite',
      color: '#fff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, opacity: 0.15, pointerEvents: 'none' }}>
        {[...Array(50)].map((_, i) => (
          <div key={i} style={{
            position: 'absolute', width: `${Math.random() * 4 + 2}px`, height: `${Math.random() * 4 + 2}px`,
            background: `hsl(${200 + Math.random() * 60}, 70%, ${60 + Math.random() * 20}%)`,
            borderRadius: '50%', left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`,
            animation: `float ${5 + Math.random() * 10}s ease-in-out infinite`, animationDelay: `${Math.random() * 5}s`
          }} />
        ))}
      </div>

      <AdminAppBar />
      
      <div style={{ position: 'relative', zIndex: 1, padding: '24px', maxWidth: '1400px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
        <div style={{ position: 'relative', padding: '40px 24px', marginBottom: '20px', textAlign: 'center', overflow: 'hidden' }}>
          <h1 style={{
            fontSize: 'clamp(2.5rem, 6vw, 4rem)', fontWeight: '900', marginBottom: '16px',
            background: 'linear-gradient(135deg, #10b981 0%, #06b6d4 30%, #3b82f6 60%, #8b5cf6 100%)',
            backgroundSize: '200% 200%', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            backgroundClip: 'text', lineHeight: '1.15', letterSpacing: '-0.03em',
            animation: 'gradientShift 5s ease infinite', textShadow: '0 0 80px rgba(16, 185, 129, 0.3)'
          }}>Stock Management</h1>
          <div style={{ width: '120px', height: '4px', background: 'linear-gradient(90deg, transparent, #10b981, #06b6d4, transparent)', margin: '0 auto', borderRadius: '2px', boxShadow: '0 0 20px rgba(16, 185, 129, 0.5)' }}></div>
        </div>

        {renderTabs()}

        <div style={{ marginBottom: '32px', position: 'relative', maxWidth: '600px', margin: '0 auto 32px', width: '100%' }}>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', filter: 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.3))' }}>
            <FaSearch style={{ position: 'absolute', right: '20px', color: searchQuery ? '#10b981' : '#64748b', zIndex: 2, transition: 'all 0.3s ease', filter: searchQuery ? 'drop-shadow(0 0 8px rgba(16, 185, 129, 0.6))' : 'none' }} size={20} />
            <input type="text" placeholder="🔍 Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%', padding: searchQuery ? '18px 60px 18px 56px' : '18px 60px 18px 24px',
                background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.98), rgba(51, 65, 85, 0.9))', backdropFilter: 'blur(20px)',
                border: `1.5px solid ${searchQuery ? 'rgba(16, 185, 129, 0.6)' : 'rgba(71, 85, 105, 0.5)'}`,
                borderRadius: '20px', color: 'white', fontSize: '16px', fontWeight: '500', outline: 'none', transition: 'all 0.35s ease',
                boxShadow: searchQuery ? '0 10px 40px rgba(16, 185, 129, 0.25), inset 0 0 0 1px rgba(16, 185, 129, 0.4)' : '0 6px 20px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255,255,255,0.05)',
                boxSizing: 'border-box'
              }}
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')}
                style={{
                  position: 'absolute', left: '16px', background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.3), rgba(220, 38, 38, 0.3))',
                  backdropFilter: 'blur(10px)', border: '1px solid rgba(239, 68, 68, 0.4)', borderRadius: '50%', width: '32px', height: '32px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#f87171', transition: 'all 0.3s ease', zIndex: 2
                }}
              ><FaTimes size={14} /></button>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))', gap: '24px', width: '100%' }}>
          {filteredItems.map((item, index) => {
            const status = getStatusInfo(item);
            return (
            <div key={item.id}
              style={{
                background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.9), rgba(51, 65, 85, 0.7))', backdropFilter: 'blur(20px)',
                border: `1.5px solid ${status.border}`, borderRadius: '20px', padding: '24px',
                transition: 'all 0.4s ease', boxShadow: '0 10px 40px rgba(0, 0, 0, 0.4)', position: 'relative', overflow: 'hidden'
              }}
            >
              {activeTab === 'guppies' ? (
                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                  {item.maleimage && (
                    <div style={{ flex: 1, height: '140px', borderRadius: '12px', overflow: 'hidden', background: 'rgba(0,0,0,0.2)' }}>
                      <img src={item.maleimage} alt="Male" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                  )}
                  {item.femaleimage && (
                    <div style={{ flex: 1, height: '140px', borderRadius: '12px', overflow: 'hidden', background: 'rgba(0,0,0,0.2)' }}>
                      <img src={item.femaleimage} alt="Female" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                  )}
                </div>
              ) : (
                item.image && (
                  <div style={{
                    width: '100%', height: '140px', marginBottom: '16px', borderRadius: '12px', overflow: 'hidden',
                    background: 'rgba(0,0,0,0.2)'
                  }}>
                    <img src={item.image} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                )
              )}
              
              <div style={{
                position: 'absolute', top: '16px', right: '16px', padding: '6px 12px', borderRadius: '20px',
                background: status.bg, border: `1px solid ${status.border}`, fontSize: '12px', fontWeight: '700',
                color: status.color, display: 'flex', alignItems: 'center', gap: '6px', zIndex: 2
              }}>
                {status.isSoldOut ? <FaTimes size={10} /> : <FaCheckCircle size={10} />} {status.text}
              </div>

              <h3 style={{ fontSize: '1.5rem', marginBottom: '16px', color: '#fff', fontWeight: '800', wordBreak: 'break-word' }}>{item.name}</h3>
              
              {renderCardDetails(item)}

              <div style={{ display: 'flex', gap: '10px', marginTop: 'auto' }}>
                <button onClick={() => openEditForm(item)}
                  style={{ flex: 1, padding: '12px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', borderRadius: '12px', cursor: 'pointer', color: 'white', fontSize: '14px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                ><FaEdit size={14} /> Edit</button>
                <button onClick={() => handleDeleteItem(item)} disabled={deletingItemId === item.id}
                  style={{ flex: 1, padding: '12px', background: deletingItemId === item.id ? '#475569' : 'linear-gradient(135deg, #ef4444, #dc2626)', border: 'none', borderRadius: '12px', cursor: deletingItemId === item.id ? 'wait' : 'pointer', color: 'white', fontSize: '14px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                ><FaTrash size={14} /> {deletingItemId === item.id ? 'Deleting...' : (showDeleteConfirm === item.id ? 'Confirm' : 'Delete')}</button>
              </div>
            </div>
          )})}
        </div>

        {filteredItems.length === 0 && (
          <div style={{ textAlign: 'center', padding: '80px 24px', background: 'rgba(30, 41, 59, 0.7)', borderRadius: '24px', marginTop: '40px' }}>
            <div style={{ fontSize: '4rem', marginBottom: '20px' }}>🔍</div>
            <h3 style={{ fontSize: '1.75rem', fontWeight: '900', color: '#10b981', marginBottom: '12px' }}>No Items Found</h3>
          </div>
        )}

        {editingItem && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px' }} onClick={(e) => { if(e.target===e.currentTarget) setEditingItem(null); }}>
            <div style={{ background: 'rgba(30, 41, 59, 0.98)', padding: '32px', borderRadius: '24px', width: '100%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: '800', color: '#fff' }}>Edit {activeTab === 'food' ? 'Food' : activeTab.slice(0,-1)}</h2>
                <button type="button" onClick={() => setEditingItem(null)} style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' }}><FaTimes size={24} /></button>
              </div>
              <form onSubmit={handleSubmit}>
                {renderEditFields()}
                <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                  <button type="button" onClick={() => setEditingItem(null)} style={{ flex: 1, padding: '14px', background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: 'white', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
                  <button type="submit" disabled={savingItemId === editingItem.id} style={{ flex: 2, padding: '14px', background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none', borderRadius: '12px', color: 'white', fontWeight: '700', cursor: savingItemId ? 'wait' : 'pointer' }}>
                    {savingItemId === editingItem.id ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AdminHomeScreen;